import useDocumentTitle from "hooks/useDocumentTitle";
import { sizer } from "layout/styles/styled/sizer";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { confirmDialog } from "primereact/confirmdialog";
import { DataTable } from "primereact/datatable";
import { InputText } from "primereact/inputtext";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";

import {
  useDeleteCustomCptVolumeMutation,
  useGetCustomCptVolumeByIdQuery,
  useLazyGetCustomCptVolumeDownloadUrlQuery,
  useUpdateCustomCptVolumeMutation,
} from "../../store/queries/customCptVolume";
import { ICustomCptVolumeRow } from "../../types/customCptVolume";
import {
  SpecialtyList,
  SpecialtyListDict,
} from "../../types/Provider/Provider";
import { CUSTOM_CPT_VOLUME_TAB_INDEX } from "./utils/constants";

const PageContainer = styled.div`
  padding: ${sizer(4)};
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: ${sizer(2)};
  margin-bottom: ${sizer(3)};
`;

const MetaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: ${sizer(2)};
  margin-bottom: ${sizer(3)};
  font-size: 0.95rem;

  .label {
    color: #6c757d;
    display: block;
    font-size: 0.8rem;
    margin-bottom: ${sizer(0.5)};
  }
  .value {
    color: #212529;
    font-weight: 600;
  }
`;

function extractApiErrorMessage(error: unknown): string {
  const e = error as {
    data?: { message?: string };
    error?: { data?: { message?: string } };
    message?: string;
  };
  if (e?.data?.message) return e.data.message;
  if (e?.error?.data?.message) return e.error.data.message;
  if (e?.message) return e.message;
  return "Request failed";
}

export default function CustomCptVolumeDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useRef<Toast>(null);
  const [labelEdit, setLabelEdit] = useState("");

  useDocumentTitle("Custom CPT volume");

  const { data, isLoading, refetch } = useGetCustomCptVolumeByIdQuery(
    id ?? "",
    { skip: !id }
  );
  const record = data?.data;
  const rows = record?.rows ?? [];

  const [updateUpload] = useUpdateCustomCptVolumeMutation();
  const [deleteUpload] = useDeleteCustomCptVolumeMutation();
  const [fetchDownloadUrl] = useLazyGetCustomCptVolumeDownloadUrlQuery();

  useEffect(() => {
    if (record?.label != null) setLabelEdit(record.label);
  }, [record?.label]);

  const handleBack = () => {
    navigate(`/payer-rules?tab=${CUSTOM_CPT_VOLUME_TAB_INDEX}`);
  };

  const handleDownload = async () => {
    if (!id) return;
    try {
      const res = await fetchDownloadUrl(id).unwrap();
      if (res.url) window.open(res.url, "_blank", "noopener,noreferrer");
    } catch (err) {
      toast.current?.show({
        severity: "error",
        summary: "Download failed",
        detail: extractApiErrorMessage(err),
      });
    }
  };

  const saveLabel = async () => {
    if (!id || !record) return;
    const trimmed = labelEdit.trim();
    if (!trimmed || trimmed === record.label) return;
    try {
      await updateUpload({ id, label: trimmed }).unwrap();
      toast.current?.show({
        severity: "success",
        summary: "Saved",
        detail: "Label updated.",
      });
      refetch();
    } catch (err) {
      toast.current?.show({
        severity: "error",
        summary: "Save failed",
        detail: extractApiErrorMessage(err),
      });
    }
  };

  const handleToggleActive = () => {
    if (!id || !record) return;
    const next = !record.isActive;
    const run = async () => {
      try {
        await updateUpload({ id, isActive: next }).unwrap();
        toast.current?.show({
          severity: "success",
          summary: next ? "Activated" : "Deactivated",
          detail: next
            ? "This upload is now active for its account and specialty."
            : "Override deactivated.",
        });
        refetch();
      } catch (err) {
        toast.current?.show({
          severity: "error",
          summary: "Update failed",
          detail: extractApiErrorMessage(err),
        });
      }
    };
    if (!next) {
      confirmDialog({
        message:
          "Deactivate this upload? Smart Insights will use default volume sources until another upload is active for this account and specialty.",
        header: "Deactivate",
        icon: "pi pi-exclamation-triangle",
        accept: run,
      });
    } else {
      run().catch(() => {});
    }
  };

  const handleArchive = () => {
    if (!id) return;
    confirmDialog({
      message:
        "Archive this upload? It will no longer apply to Smart Insights volume for this scope.",
      header: "Archive upload",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: async () => {
        try {
          await deleteUpload(id).unwrap();
          toast.current?.show({
            severity: "success",
            summary: "Archived",
            detail: "Upload archived.",
          });
          handleBack();
        } catch (err) {
          toast.current?.show({
            severity: "error",
            summary: "Archive failed",
            detail: extractApiErrorMessage(err),
          });
        }
      },
    });
  };

  const specialtyLabel =
    record &&
    (SpecialtyListDict[record.specialty as SpecialtyList] ?? record.specialty);

  return (
    <PageContainer>
      <Toast ref={toast} />
      <HeaderRow>
        <div className="flex align-items-center gap-2 flex-wrap">
          <Button
            type="button"
            icon="pi pi-arrow-left"
            className="p-button-text"
            label="Back"
            onClick={handleBack}
          />
          <h1 className="text-2xl font-semibold m-0">Custom CPT volume</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            type="button"
            icon="pi pi-download"
            label="Download original"
            onClick={() => {
              handleDownload().catch(() => {});
            }}
            disabled={!record}
          />
          <Button
            type="button"
            icon={
              record?.isActive ? "pi pi-times-circle" : "pi pi-check-circle"
            }
            label={record?.isActive ? "Deactivate" : "Set active"}
            className="p-button-outlined"
            onClick={handleToggleActive}
            disabled={!record}
          />
          <Button
            type="button"
            icon="pi pi-trash"
            label="Archive"
            className="p-button-danger p-button-outlined"
            onClick={handleArchive}
            disabled={!record}
          />
        </div>
      </HeaderRow>

      {!id && (
        <Card>
          <p>Missing upload id.</p>
        </Card>
      )}

      {isLoading && <p>Loading…</p>}

      {record && (
        <>
          <Card className="mb-3">
            <MetaGrid>
              <div>
                <span className="label">Account</span>
                <span className="value">{record.account?.name ?? "—"}</span>
              </div>
              <div>
                <span className="label">Specialty</span>
                <span className="value">{specialtyLabel}</span>
              </div>
              <div>
                <span className="label">Status</span>
                <span className="value">
                  {record.isActive ? (
                    <Tag severity="success" value="Active" />
                  ) : (
                    <Tag severity="info" value="Inactive" />
                  )}
                </span>
              </div>
              <div>
                <span className="label">Original file</span>
                <span className="value">{record.originalFileName}</span>
              </div>
              <div>
                <span className="label">CPT count / total volume</span>
                <span className="value">
                  {record.totalCpts.toLocaleString()} /{" "}
                  {record.totalVolume.toLocaleString()}
                </span>
              </div>
              <div>
                <span className="label">Created</span>
                <span className="value">
                  {new Date(record.createdAt).toLocaleString()}
                </span>
              </div>
            </MetaGrid>
            <div className="flex flex-column gap-2 md:flex-row md:align-items-end">
              <div className="flex-grow-1">
                <label className="block text-600 text-sm mb-1">Label</label>
                <InputText
                  value={labelEdit}
                  onChange={(e) => setLabelEdit(e.target.value)}
                  className="w-full"
                />
              </div>
              <Button
                type="button"
                label="Save label"
                icon="pi pi-save"
                onClick={() => {
                  saveLabel().catch(() => {});
                }}
                disabled={
                  !labelEdit.trim() || labelEdit.trim() === record.label
                }
              />
            </div>
          </Card>

          <Card title="Parsed rows (volume descending from server)">
            <DataTable
              value={rows}
              dataKey="cptCode"
              paginator
              rows={50}
              rowsPerPageOptions={[25, 50, 100]}
              removableSort
              emptyMessage="No rows."
            >
              <Column
                field="cptCode"
                header="CPT / HCPCS"
                sortable
                style={{ minWidth: "8rem" }}
              />
              <Column field="description" header="Description" sortable />
              <Column
                field="volume"
                header="Volume"
                sortable
                body={(r: ICustomCptVolumeRow) => r.volume.toLocaleString()}
              />
            </DataTable>
          </Card>
        </>
      )}
    </PageContainer>
  );
}
